import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import { DeleteIcon, PenIcon, TestIcon } from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'

import { updateLanguageTestTemplate } from '../../../api/language-test/update-language-test-template'
import { Paths } from '../../../lib/constants/path'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { LanguageTestTemplate } from '../../../lib/types/models/language-test/language-test-template'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { getTranslatedName } from '../../../lib/utils/functions/translation.functions'
import { queryClient } from '../../../main'
import { LanguageTestProperties } from '../../../views/language-test/utils/language-test-properties'
import CountryFlag from '../../elements/country-flag'
import LanguageTestTemplateDeleteModal from './language-test-template-delete-modal'
import LanguageTestTemplateForm from './language-test-template-form'

interface LanguageTestCardProps {
  template: LanguageTestTemplate
}

const LagnuageTestCard = ({ template }: LanguageTestCardProps) => {
  const { navigate } = useNavigator()

  const { toggleModal, closeModel, modalState } = useModalManager<
    'language-test-template-delete-modal' | 'language-test-template-edit-modal'
  >(['language-test-template-delete-modal', 'language-test-template-edit-modal'])

  const toggleDeleteModal = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    toggleModal('language-test-template-delete-modal')
  }

  const toggleEditModal = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    toggleModal('language-test-template-edit-modal')
  }

  const { mutateAsync: updateTemplate } = useMutation({
    mutationFn: updateLanguageTestTemplate,
    onError: showAxiosError,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['language-test-templates'] })
      closeModel('language-test-template-edit-modal')
      toast.success('Test de langue modifié avec succès')
    }
  })

  return (
    <div
      className="border cursor-pointer border-grey-light rounded-lg p-4 flex flex-col justify-between gap-2 w-full hover:shadow-[0px_12px_26px_0px_#93939333] transition-all duration-300"
      onClick={() => {
        navigate(
          Paths.LANGUAGE_TEST_TEMPLATE_DETAILS_PAGE.replace(':id', template.id.toString()),
          {
            state: { template }
          }
        )
      }}
    >
      <div className="flex justify-between items-center">
        <div className="flex flex-col gap-2">
          <Typography variant="tag" className="cursor-text" onClick={(e) => e.stopPropagation()}>
            {template.name}
          </Typography>
          {Array.isArray(template.formulas) && template.formulas.length > 0 && (
            <Typography
              variant="label-semibold"
              color={tailwindColors.grey.light.active}
              className="line-clamp-2 text-ellipsis mr-3"
              title={template.formulas.map((formula) => getTranslatedName(formula)).join(', ')}
            >
              Formulas :{' '}
              {template.formulas.map((formula) => getTranslatedName(formula)).join(', ')}
            </Typography>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Badge text={DateFormatter.DDMMYYYY(template.createdAt)} variant="grey" size="medium" />
          <Button
            variant="destructive"
            icon={DeleteIcon}
            size="small"
            className="px-1.5"
            onClick={toggleDeleteModal}
          />
          <Button
            variant="ghost"
            icon={PenIcon}
            size="small"
            className="px-1.5"
            onClick={toggleEditModal}
          />
        </div>
      </div>

      <div className="flex items-center gap-2 w-full">
        <div className="flex items-center gap-3 bg-white-active rounded p-3 flex-wrap">
          {template.countries.map((country) => (
            <CountryFlag key={country.code} code={country.code} size="large" />
          ))}
        </div>

        <Badge
          className="whitespace-nowrap"
          size="medium"
          text={`${template.sections.length ?? 0} parties - ${LanguageTestProperties.getQuestionsCount(template.sections)} questions`}
          variant="primaryLight"
        />
      </div>

      <div onClick={(e) => e.stopPropagation()}>
        <LanguageTestTemplateDeleteModal
          template={template}
          open={modalState['language-test-template-delete-modal']}
          onClose={() => closeModel('language-test-template-delete-modal')}
        />
      </div>

      <div onClick={(e) => e.stopPropagation()}>
        <Modal
          icon={TestIcon}
          isOpen={modalState['language-test-template-edit-modal']}
          onCancel={() => closeModel('language-test-template-edit-modal')}
          title="Modifier le test de langue"
          hideActions
        >
          <LanguageTestTemplateForm
            className={MODAL_BODY_CLASSNAME}
            defaultValues={LanguageTestProperties.mapLanguageTestTemplateToInput(template)}
            buttonsConfig={modalButtonConfig({
              onCancel: () => closeModel('language-test-template-edit-modal'),
              onConfirm: async (data) => updateTemplate({ id: template.id, ...data })
            })}
          />
        </Modal>
      </div>
    </div>
  )
}

export default LagnuageTestCard
